<div class="main-content">
  <div class="container-fluid">
    <div class="admin-page-header">
      <div>
        <h3>Application — Proposal Support Service</h3>
        <p class="text-muted">Fields marked <span class="req">*</span> are required.</p>
      </div>
    </div>

    <div *ngIf="errorMessage" class="alert alert-danger">{{ errorMessage }}</div>

    <form-error-summary [errors]="fieldErrors" (jumpTo)="jumpToField($event)"></form-error-summary>

    <form [formGroup]="form" (ngSubmit)="onSubmit()">

      <!-- ── Q1. TELL US ABOUT YOUR FUNDING SCHEME ──────────────────────────── -->
      <div class="form-section">
        <h4 class="form-section-title">Q1. Tell us about your funding scheme</h4>
        <p class="text-muted">Please provide information about the type of funding and project call that applies to your future project. Some fields depend on whether you seek EU-funding or national/regional funding.</p>

        <div class="form-group" id="field-funding_type">
          <label>Will you seek project funding through an EU programme or through a national/regional programme? <span class="req">*</span></label>
          <div>
            <label class="radio-inline">
              <input type="radio" formControlName="funding_type" value="eu"> EU-funded project
            </label>
            <label class="radio-inline">
              <input type="radio" formControlName="funding_type" value="national"> Nationally or regionally funded project
            </label>
          </div>
        </div>

        <div *ngIf="isEuFunded" class="row">
          <div class="col-md-6">
            <div class="form-group" id="field-funding_programme">
              <label>Which EU programme will you seek project funding through? <span class="req">*</span></label>
              <select class="form-control"
                      [class.input-error]="showEuFundingError()"
                      formControlName="funding_programme">
                <option value="">— Select —</option>
                <option *ngFor="let p of EU_FUNDING_PROGRAMMES" [value]="p">{{ p }}</option>
              </select>
              <div *ngIf="showEuFundingError()" class="field-error">This field is required.</div>
            </div>
          </div>
          <div class="col-md-6" *ngIf="isOtherFundingProgramme">
            <div class="form-group">
              <label>Please specify the funding programme</label>
              <input type="text" class="form-control" formControlName="other_funding_programme">
            </div>
          </div>
        </div>

        <div *ngIf="!isEuFunded" class="row">
          <div class="col-md-6">
            <div class="form-group" id="field-national_programme_name">
              <label>Please name your national or regional funding programme <span class="req">*</span></label>
              <input type="text" class="form-control"
                     [class.input-error]="showNationalNameError()"
                     formControlName="national_programme_name">
              <div *ngIf="showNationalNameError()" class="field-error">This field is required.</div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group" id="field-national_programme_country">
              <label>Select the country under which your national or regional funding programme operates <span class="req">*</span></label>
              <select class="form-control"
                      [class.input-error]="showNationalCountryError()"
                      formControlName="national_programme_country">
                <option value="">— Select —</option>
                <option *ngFor="let c of COUNTRIES" [value]="c">{{ c }}</option>
              </select>
              <div *ngIf="showNationalCountryError()" class="field-error">This field is required.</div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <div class="form-group" id="field-project_call_topic_id">
              <label>The code of the call from which you will seek project funding (or the equivalent call, scheme, or programme reference) <span class="req">*</span></label>
              <input type="text" class="form-control"
                     [class.input-error]="formSubmitted && form.get('project_call_topic_id')?.invalid"
                     formControlName="project_call_topic_id"
                     placeholder="e.g. HORIZON-CL4-2023-TWIN-TRANSITION-01">
              <div *ngIf="formSubmitted && form.get('project_call_topic_id')?.invalid" class="field-error">This field is required.</div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group" id="field-coordinator_country">
              <label>In which country will your future project's coordinating organization be based? <span class="req">*</span></label>
              <select class="form-control"
                      [class.input-error]="formSubmitted && form.get('coordinator_country')?.invalid"
                      formControlName="coordinator_country">
                <option value="">— Select —</option>
                <option *ngFor="let c of COUNTRIES" [value]="c">{{ c }}</option>
              </select>
              <div *ngIf="formSubmitted && form.get('coordinator_country')?.invalid" class="field-error">This field is required.</div>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Q2. EXPLAIN THE SUPPORT YOU ARE REQUESTING ─────────────────────── -->
      <div class="form-section">
        <h4 class="form-section-title">Q2. Explain the support you are requesting</h4>
        <p class="text-muted">Please explain your reason for applying for Proposal Support Service, your expectations and the objectives of your future project.</p>

        <div class="form-group" id="field-why_the_project_applying">
          <label>Why are you applying for Proposal Support Service? <span class="req">*</span></label>
          <textarea class="form-control" rows="4"
                    [class.input-error]="formSubmitted && form.get('why_the_project_applying')?.invalid"
                    formControlName="why_the_project_applying"></textarea>
          <div *ngIf="formSubmitted && form.get('why_the_project_applying')?.invalid" class="field-error">This field is required.</div>
        </div>

        <div class="form-group" id="field-support_expectations">
          <label>Describe what you expect from HSbooster.eu II proposal support <span class="req">*</span></label>
          <textarea class="form-control" rows="4"
                    [class.input-error]="formSubmitted && form.get('support_expectations')?.invalid"
                    formControlName="support_expectations"></textarea>
          <div *ngIf="formSubmitted && form.get('support_expectations')?.invalid" class="field-error">This field is required.</div>
        </div>

        <div class="form-group" id="field-project_short_description">
          <label>Summarize your future project's objectives, context, and expected results. Include planned activities, budget allocation and expected perspectives after the project. <span class="req">*</span></label>
          <textarea class="form-control" rows="5"
                    [class.input-error]="formSubmitted && form.get('project_short_description')?.invalid"
                    formControlName="project_short_description"></textarea>
          <div *ngIf="formSubmitted && form.get('project_short_description')?.invalid" class="field-error">This field is required.</div>
        </div>

        <div class="form-group" id="field-one_pager">
          <label>Upload a 1-pager of your future project <span class="req">*</span></label>
          <div class="cv-upload-area" [class.cv-upload-area--error]="formSubmitted && !onePagerFileId">
            <input type="file" accept="application/pdf" #onePagerInput style="display:none"
                   (change)="onOnePagerFileChange($event)">
            <button type="button" class="btn btn-default btn-sm"
                    (click)="onePagerInput.click()" [disabled]="onePagerUploading">
              <i class="fa" [ngClass]="onePagerUploading ? 'fa-spinner fa-spin' : 'fa-upload'"></i>
              {{ onePagerFilename ? 'Replace PDF' : 'Upload PDF' }}
            </button>
            <span *ngIf="onePagerFilename" class="cv-status cv-status--ok">
              <i class="fa fa-file-pdf-o"></i> {{ onePagerFilename }}
            </span>
            <span *ngIf="onePagerUploading" class="cv-status">Uploading...</span>
          </div>
          <div *ngIf="onePagerUploadError" class="field-error">{{ onePagerUploadError }}</div>
          <div *ngIf="formSubmitted && !onePagerFileId" class="field-error">Please upload the 1-pager before submitting.</div>
          <p class="text-muted small">Data uploaded is confidential and will not be shared beyond the designated EPEs and project coordinator.</p>
        </div>

        <div class="form-group" id="field-objectives_standardisation">
          <label>Specify what expected standardisation activities or use of standards are mentioned directly or indirectly in the call text (e.g. "standards", "market requirements", "technical specification", "state-of-the-art benchmarks") <span class="req">*</span></label>
          <textarea class="form-control" rows="4"
                    [class.input-error]="formSubmitted && form.get('objectives_standardisation')?.invalid"
                    formControlName="objectives_standardisation"></textarea>
          <div *ngIf="formSubmitted && form.get('objectives_standardisation')?.invalid" class="field-error">This field is required.</div>
        </div>

        <div class="form-group" id="field-topics">
          <label>Specify what topics apply to your future project <span class="req">*</span></label>
          <div class="topics-picker" [class.topics-picker--error]="formSubmitted && selectedTopics.length === 0">
            <div class="topics-selected">
              <span class="topics-count" [class.topics-count--full]="selectedTopics.length === MAX_TOPICS">
                {{ selectedTopics.length }}/{{ MAX_TOPICS }} selected
              </span>
              <ng-container *ngIf="selectedTopics.length > 0; else noTopics">
                <span *ngFor="let t of selectedTopics" class="topic-tag">
                  <span class="topic-tag-group">{{ t.groupLabel }}</span>
                  {{ t.label }}
                  <span class="topic-tag-remove" (click)="removeTopic(t.value)" title="Remove">&#x2715;</span>
                </span>
              </ng-container>
              <ng-template #noTopics>
                <span class="topics-empty">No topics selected yet — expand a category below.</span>
              </ng-template>
            </div>

            <div class="topics-tree">
              <div *ngFor="let group of TOPIC_GROUPS" class="tg">
                <div class="tg-header" (click)="toggleGroup(group.label)">
                  <i class="fa fa-fw" [ngClass]="isGroupExpanded(group.label) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                  <strong>{{ group.label }}</strong>
                </div>
                <div *ngIf="isGroupExpanded(group.label)" class="tg-body">
                  <div *ngFor="let sub of group.subGroups" class="tg-sub">
                    <div class="tg-sub-header">
                      <span class="tg-expand" (click)="toggleSubGroup(sub.value)">
                        <i class="fa fa-fw" [ngClass]="isSubGroupExpanded(sub.value) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                      </span>
                      <span class="tg-item"
                            [class.tg-item--selected]="isTopicSelected(sub.value)"
                            [class.tg-item--disabled]="!canSelectMore() && !isTopicSelected(sub.value)"
                            (click)="selectTopic(sub.value, sub.label, group.label)">
                        {{ sub.label }}
                      </span>
                    </div>
                    <div *ngIf="isSubGroupExpanded(sub.value)" class="tg-children">
                      <div *ngFor="let child of sub.children" class="tg-child">
                        <span class="tg-item tg-item--child"
                              [class.tg-item--selected]="isTopicSelected(child.value)"
                              [class.tg-item--disabled]="!canSelectMore() && !isTopicSelected(child.value)"
                              (click)="selectTopic(child.value, child.label, group.label)">
                          {{ child.label }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div *ngIf="formSubmitted && selectedTopics.length === 0" class="field-error">Please select at least one topic.</div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <div class="form-group" id="field-trl_start">
              <label>Describe the TRL starting point <span class="req">*</span></label>
              <select class="form-control"
                      [class.input-error]="formSubmitted && form.get('trl_start')?.invalid"
                      formControlName="trl_start">
                <option value="">— Select —</option>
                <option *ngFor="let t of TRL_OPTIONS" [value]="t">TRL {{ t }}</option>
              </select>
              <div *ngIf="formSubmitted && form.get('trl_start')?.invalid" class="field-error">This field is required.</div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group" id="field-trl_target">
              <label>Describe the expected TRL after finalizing the future project <span class="req">*</span></label>
              <select class="form-control"
                      [class.input-error]="formSubmitted && form.get('trl_target')?.invalid"
                      formControlName="trl_target">
                <option value="">— Select —</option>
                <option *ngFor="let t of TRL_OPTIONS" [value]="t">TRL {{ t }}</option>
              </select>
              <div *ngIf="formSubmitted && form.get('trl_target')?.invalid" class="field-error">This field is required.</div>
            </div>
          </div>
        </div>

        <div class="form-group">
          <label>Are there any current or upcoming regulations that apply to your future project that you know of?</label>
          <textarea class="form-control" rows="3" formControlName="applicable_regulations"></textarea>
        </div>

        <div class="row">
          <div class="col-md-6">
            <div class="form-group" id="field-call_deadline">
              <label>What is the deadline of the project call (final date of submission)? <span class="req">*</span></label>
              <input type="date" class="form-control"
                     [class.input-error]="formSubmitted && form.get('call_deadline')?.invalid"
                     formControlName="call_deadline">
              <div *ngIf="formSubmitted && form.get('call_deadline')?.invalid" class="field-error">This field is required.</div>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Q3. STANDARDS / SDO KNOWLEDGE ──────────────────────────────────── -->
      <div class="form-section">
        <h4 class="form-section-title">Q3. Do you already know of relevant standards or Standards Developing Organisations?</h4>
        <p class="text-muted">Please specify your level of knowledge about standards and standardisation. If you have already identified Standards Developing Organisations or standards of interest, please provide details below.</p>

        <div class="form-group">
          <label>Do you know about standards and standardisation, and do you have knowledge about standardisation processes? <span class="req">*</span></label>
          <div>
            <label class="radio-inline"><input type="radio" formControlName="know_standards" value="no"> No</label>
            <label class="radio-inline"><input type="radio" formControlName="know_standards" value="yes"> Yes</label>
          </div>
          <div *ngIf="knowsStandards" class="form-group" style="margin-top: 8px;">
            <textarea class="form-control" rows="3" formControlName="standards_details" placeholder="Please provide more details…"></textarea>
          </div>
        </div>

        <div class="form-group">
          <label>Have you been in contact with a National Standardisation Body? <span class="req">*</span></label>
          <div>
            <label class="radio-inline"><input type="radio" formControlName="nsb_contact" value="no"> No</label>
            <label class="radio-inline"><input type="radio" formControlName="nsb_contact" value="yes"> Yes</label>
          </div>
          <div *ngIf="hasNsbContact" class="form-group" style="margin-top: 8px;">
            <input type="text" class="form-control" formControlName="nsb_contact_name" placeholder="Name of the National Standardisation Body">
          </div>
        </div>

        <div class="form-group">
          <label>Do you know of standards that are relevant for your future project? <span class="req">*</span></label>
          <div>
            <label class="radio-inline"><input type="radio" formControlName="known_standards_yn" value="no"> No</label>
            <label class="radio-inline"><input type="radio" formControlName="known_standards_yn" value="yes"> Yes</label>
          </div>
          <div *ngIf="knowsRelevantStandards" class="form-group" style="margin-top: 8px;">
            <input type="text" class="form-control" formControlName="known_standards_titles" placeholder="Titles of the standards">
          </div>
        </div>

        <div formArrayName="standard_reference">
          <div *ngFor="let refGroup of standardReference.controls; let i = index"
               [formGroupName]="i" class="panel panel-default entry-panel">
            <div class="panel-heading">
              <strong>Standards body and technical committee</strong>
              <button type="button" class="btn btn-danger btn-xs pull-right" (click)="removeStandardReference(i)">Remove</button>
            </div>
            <div class="panel-body">
              <div class="form-group">
                <label>Select the National Standardisation Body you would like to get in touch with or discover more about</label>
                <select class="form-control" formControlName="sdo">
                  <option value="">— Select —</option>
                  <optgroup *ngFor="let grp of SDO_OPTIONS" [label]="grp.group">
                    <option *ngFor="let opt of grp.options" [value]="opt">{{ opt }}</option>
                  </optgroup>
                </select>
              </div>
              <div *ngIf="isOtherSdo(refGroup)" class="form-group">
                <label>Specify Standards Developing Organisation</label>
                <input type="text" class="form-control" formControlName="other_options">
              </div>
              <div class="row">
                <div class="col-md-5">
                  <div class="form-group">
                    <label>Technical committee, sub-committee, or working group code</label>
                    <input type="text" class="form-control" formControlName="tc_number" placeholder="e.g. ISO/IEC JTC 1/SC 27">
                  </div>
                </div>
                <div class="col-md-7">
                  <div class="form-group">
                    <label>Full name of the committee</label>
                    <input type="text" class="form-control" formControlName="tc_title" placeholder="e.g. Information security, cybersecurity and privacy protection">
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <button type="button" class="btn btn-default btn-sm" (click)="addStandardReference()">
          <i class="fa fa-plus"></i> Add a standards body / committee
        </button>
      </div>

      <!-- ── Q4. AVAILABILITY AND LANGUAGE PREFERENCE ───────────────────────── -->
      <div class="form-section">
        <h4 class="form-section-title">Q4. Indicate your availability and language preference</h4>
        <p class="text-muted">Please indicate when you would like this support to start and whether you have scheduling constraints. Also tell us in what language(s) you would like to receive support and your time zone.</p>

        <div class="row">
          <div class="col-md-6">
            <div class="form-group" id="field-support_start">
              <label>When would you like this support to start? <span class="req">*</span></label>
              <select class="form-control"
                      [class.input-error]="formSubmitted && form.get('support_start')?.invalid"
                      formControlName="support_start">
                <option value="">— Select —</option>
                <option *ngFor="let opt of SUPPORT_START_OPTIONS" [value]="opt">{{ opt }}</option>
              </select>
              <div *ngIf="formSubmitted && form.get('support_start')?.invalid" class="field-error">This field is required.</div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label>Scheduling constraints (e.g. holidays or other commitments)</label>
              <input type="text" class="form-control" formControlName="scheduling_constraints">
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <div class="form-group" id="field-preferred_languages">
              <label>In which language(s) would you like to receive support? English is always available. <span class="req">*</span></label>
              <input type="text" class="form-control"
                     [class.input-error]="formSubmitted && form.get('preferred_languages')?.invalid"
                     formControlName="preferred_languages"
                     placeholder="e.g. English, French, German">
              <div *ngIf="formSubmitted && form.get('preferred_languages')?.invalid" class="field-error">This field is required.</div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group" id="field-time_zone">
              <label>Time zone <span class="req">*</span></label>
              <input type="text" class="form-control"
                     [class.input-error]="formSubmitted && form.get('time_zone')?.invalid"
                     formControlName="time_zone"
                     placeholder="e.g. CET/CEST">
              <div *ngIf="formSubmitted && form.get('time_zone')?.invalid" class="field-error">This field is required.</div>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Q5. CONSENT AND DECLARATIONS ───────────────────────────────────── -->
      <div class="form-section">
        <h4 class="form-section-title">Q5. Consent and declarations</h4>
        <p class="text-muted">Please review and confirm the following before submitting your application.</p>

        <div class="form-group" id="field-data_consent">
          <label class="checkbox-inline" [class.input-error]="formSubmitted && form.get('data_consent')?.invalid">
            <input type="checkbox" formControlName="data_consent">
            I consent to the processing of the data provided in this application for the assessment, management and delivery of HSbooster.eu II services. <span class="req">*</span>
          </label>
          <div *ngIf="formSubmitted && form.get('data_consent')?.invalid" class="field-error">This declaration is required.</div>
        </div>

        <div class="form-group" id="field-accuracy_declaration">
          <label class="checkbox-inline" [class.input-error]="formSubmitted && form.get('accuracy_declaration')?.invalid">
            <input type="checkbox" formControlName="accuracy_declaration">
            I confirm that the information provided in this application is accurate and can be verified. <span class="req">*</span>
          </label>
          <div *ngIf="formSubmitted && form.get('accuracy_declaration')?.invalid" class="field-error">This declaration is required.</div>
        </div>
      </div>

      <button type="submit" class="btn btn-primary btn-fill" [disabled]="submitting">
        <span *ngIf="submitting"><i class="fa fa-spinner fa-spin"></i> Submitting...</span>
        <span *ngIf="!submitting">Submit Application</span>
      </button>
      <button type="button" class="btn btn-default" style="margin-left: 10px;" [disabled]="savingDraft" (click)="saveDraft()">
        <span *ngIf="savingDraft"><i class="fa fa-spinner fa-spin"></i> Saving...</span>
        <span *ngIf="!savingDraft"><i class="fa fa-floppy-o"></i> Save as Draft</span>
      </button>
      <span *ngIf="draftSaved" class="text-success" style="margin-left: 10px; font-size: 13px;">
        <i class="fa fa-check"></i> Draft saved
      </span>
      <a routerLink="/my-applications" class="form-cancel-link">Cancel</a>

    </form>
  </div>
</div>
